/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/*
 * @author        zach.william@zumper.com (Zach William)
 * @copyright     Copyright (c) 2021, Zumper
 * @description   Main calendar display component
 */

import { addMonths, isEqual } from 'date-fns'
import React from 'react'

import { useCalendarMatrix } from '../../DateRangeInput/helpers/useCalendarMatrix'
import * as style from '../style'
import { UndefinedOrDate, WeekStartsOn } from '../types'

import { CalendarArrowButtons } from './CalendarArrowButtons'
import { CalendarDateButton } from './CalendarDateButton'
import { CalendarDayOfWeekHeader } from './CalendarDayOfWeekHeader'

export interface CalendarMonthsProps {
  calendarDisplayNum?: number
  dateButtonStyle?: any
  disableForeverFrom?: Date
  disableForeverTo?: Date
  disabledRanges?: ReadonlyArray<readonly [startDate: Date, endDate: Date]>
  endDate: UndefinedOrDate
  isMobileScrollCalendar?: boolean
  setDate: (date: Date) => void
  setTargetMonth: (date: Date) => void
  showDayOfWeekHeader?: boolean
  showTwoMonths?: boolean
  singleCalendar?: boolean
  startDate: UndefinedOrDate
  targetMonth: Date
  weekStartsOn: WeekStartsOn
}

export const CalendarMonths = ({
  calendarDisplayNum,
  dateButtonStyle,
  disableForeverFrom,
  disableForeverTo,
  disabledRanges,
  endDate,
  isMobileScrollCalendar,
  setDate,
  setTargetMonth,
  showDayOfWeekHeader,
  showTwoMonths,
  singleCalendar,
  startDate,
  targetMonth,
  weekStartsOn,
}: CalendarMonthsProps) => {
  // as a note all the is selected, is disabled, is today stuff could probably be
  // done in useCalendarMatrix, then just have this one render

  const nextMonth = showTwoMonths
    ? addMonths(targetMonth, 1)
    : calendarDisplayNum
      ? addMonths(targetMonth, calendarDisplayNum - 1)
      : targetMonth
  const calendarMatrix = useCalendarMatrix(targetMonth, nextMonth, weekStartsOn)

  // precalc start and end date as number
  const startDateNumber = startDate?.getTime() ?? -1
  const endDateNumber = endDate?.getTime() ?? -1

  const monthsDisplay = calendarMatrix.map(({ month, year, weeks }, index) => {
    const weeksDisplay = weeks.map((week, index) => {
      // it manually
      const start = []
      const selected = []
      const end = []
      let i = 0
      for (const day of week) {
        const currentDate = day?.getTime() ?? -1

        const isSelectedStartDate = startDateNumber === currentDate && currentDate !== -1
        const isSelectedEndDate = endDateNumber === currentDate && currentDate !== -1

        const isBeforeSelected =
          (currentDate === -1 && index === 0) ||
          (currentDate < startDateNumber && currentDate !== -1)

        const isSelected =
          (startDateNumber &&
            startDateNumber <= currentDate &&
            endDateNumber &&
            currentDate <= endDateNumber &&
            currentDate !== -1) ||
          isSelectedStartDate ||
          isSelectedEndDate

        const areStartAndEndDatesEqual = !!startDate && !!endDate && !isEqual(startDate, endDate)

        const applySelectedStartDateOnly =
          (isSelectedStartDate && !endDate) || (isSelectedStartDate && !!startDate)
        const applySelectedStartDateStyle =
          isSelectedStartDate && !singleCalendar && areStartAndEndDatesEqual
        const applySelectedEndDateStyle =
          isSelectedEndDate && areStartAndEndDatesEqual && !singleCalendar

        const component = (
          <CalendarDateButton
            currentDate={day}
            dateButtonStyle={dateButtonStyle}
            disableForeverFrom={disableForeverFrom}
            disableForeverTo={disableForeverTo}
            disabledRanges={disabledRanges}
            isSelected={isSelected}
            isSelectedEndDate={applySelectedEndDateStyle}
            isSelectedStartDate={applySelectedStartDateStyle}
            isSelectedStartDateOnly={applySelectedStartDateOnly}
            key={day?.getTime() ?? i++}
            setDate={setDate}
          />
        )

        if (isBeforeSelected) {
          start.push(component)
        } else if (isSelected) {
          selected.push(component)
        } else {
          end.push(component)
        }
      }

      return (
        <div
          css={[style.weeksContainer, singleCalendar && style.singleWeeksContainer]}
          key={week.join()}
        >
          {start}
          {selected}
          {end}
        </div>
      )
    })

    return (
      <div
        css={[
          style.monthsContainer,
          singleCalendar && style.singleMonthsContainer,
          showTwoMonths && style.twoMonthsContainer,
        ]}
        key={month}
      >
        {!isMobileScrollCalendar && (
          <CalendarArrowButtons
            hideFirstArrow={(showTwoMonths && index === 1) || !!calendarDisplayNum}
            hideSecondArrow={(showTwoMonths && index === 0) || !!calendarDisplayNum}
            setTargetMonth={setTargetMonth}
            targetMonth={targetMonth}
          >
            <div css={style.monthYearContainer}>
              {!isMobileScrollCalendar ? `${month} ${year}` : `${month}`}
            </div>
          </CalendarArrowButtons>
        )}
        {isMobileScrollCalendar && (
          <div css={style.monthYearScrollContainer}>
            {!isMobileScrollCalendar ? `${month} ${year}` : `${month}`}
          </div>
        )}
        {showDayOfWeekHeader && <CalendarDayOfWeekHeader singleCalendar={singleCalendar} />}
        {weeksDisplay}
      </div>
    )
  })

  return (
    <div css={[style.calendarContainer, showTwoMonths && style.twoMonthCalendarContainer]}>
      {monthsDisplay}
    </div>
  )
}
